import type { Character } from '../domain/character.ts';
import type { SessionState } from '../domain/session.ts';
import type { StatId } from '../domain/stats.ts';
import type { ContentRegistry } from '../content/registry.ts';
import type { Roller } from '../rules/dice.ts';
import type { RollMode } from '../rules/rollModes.ts';
import type { CheckResult } from '../rules/checks.ts';
import type { Resolution } from '../rules/degrees.ts';
import { resolveAgainstDifficulty } from '../rules/degrees.ts';
import { addRoll } from '../rules/rollHistory.ts';
import { recordUntrainedAttempt } from '../rules/sessionAdvancement.ts';
import type { RollSelection } from './rollService.ts';
import { executeRollFor, rollEvadeFor, toHistoryEntry, UNSKILLED_ID } from './rollService.ts';
import type { Effect } from '../rules/effects.ts';
import { buildEffectiveCharacter } from './effectiveCharacter.ts';
import type { EffectiveCharacter } from './effectiveCharacter.ts';
import { markSkillUsed } from './advancementService.ts';
import type { RuntimeState } from '../domain/runtime.ts';
import { resolveTrigger } from '../rules/encounter.ts';

/**
 * The one place a roll made on the Play Sheet is turned into new Character and
 * Session state.
 *
 * This exists because the consequences of a roll are rules-driven and easy to
 * forget in a component: a trained Skill Check marks the Skill for advancement,
 * an untrained one feeds the Tutorial award system instead, and every roll goes
 * into the history. Putting that in one service means a screen cannot silently
 * skip a step.
 */

export interface RollApplication {
  character: Character;
  session: SessionState;
  /**
   * The runtime state after any 'after-next-check' condition expired. Null when
   * no runtime state was supplied.
   */
  runtime: RuntimeState | null;
  /** Conditions that ended because this Check was made. */
  expiredEffectIds: string[];
  result: CheckResult;
  resolution: Resolution | null;
  /** True when this roll produced a new advancement mark. */
  marked: boolean;
}

export type PerformRollOutcome =
  | ({ ok: true } & RollApplication)
  | { ok: false; reason: string };

export interface PerformRollInput {
  character: Character;
  session: SessionState;
  selection: RollSelection;
  mode: RollMode;
  /** GM-supplied Difficulty, or null when it was not revealed. */
  difficulty: number | null;
  registry: ContentRegistry;
  now: string;
  newId: () => string;
  /** Active Buffs, Debuffs, equipment. Empty until that content exists. */
  effects?: readonly Effect[];
  /** The crawler's runtime state, so triggered conditions can expire. */
  runtime?: RuntimeState | null;
}

/**
 * The crawler as it ACTUALLY is when a Check is rolled.
 *
 * RULE DCC-INV-002 - worn gear gives bonuses, so it belongs in every
 * EffectiveCharacter a Check is resolved against, not only the one a screen
 * displays. The registry is already on the input, so this needs no new
 * dependency: the catalogue was there and simply was not being used.
 *
 * Everything that resolves a Check goes through here. A second construction
 * site is how the displayed formula and the actual roll came to disagree.
 */
function effectiveFor(input: {
  character: Character;
  effects?: readonly Effect[];
  registry: ContentRegistry;
}): EffectiveCharacter {
  return buildEffectiveCharacter(input.character, input.effects ?? [], (id) =>
    input.registry.getItem(id),
  );
}

/**
 * Rolls a Skill Check or Stat Check and applies every consequence.
 *
 * RULE DCC-ADV-SKILL-001 - a trained, eligible Skill Check marks the Skill,
 * whether it Succeeded or Failed.
 * RULE DCC-SESS-001 - an UNTRAINED attempt is recorded for the Tutorial award
 * instead, and produces no ordinary advancement mark.
 * A Stat-only Check involves no Skill and marks nothing.
 */
export function performRoll(roller: Roller, input: PerformRollInput): PerformRollOutcome {
  const effective = effectiveFor(input);
  const outcome = executeRollFor(roller, effective, input.selection, input.mode);
  if (!outcome.ok) return outcome;

  const result = outcome.result;
  const resolution =
    input.difficulty === null ? null : resolveAgainstDifficulty(result, input.difficulty);

  let character = input.character;
  let session = input.session;

  if (result.kind === 'skill' && input.selection.skill && input.selection.skill.id !== UNSKILLED_ID) {
    if (result.untrained) {
      // Tutorial-Floor untrained system, NOT ordinary advancement.
      session = recordUntrainedAttempt(session, input.selection.skill.id, character.floor);
    } else {
      character = markSkillUsed(character, input.selection.skill.id, input.registry);
    }
  }

  session = {
    ...session,
    rollHistory: addRoll(
      session.rollHistory,
      toHistoryEntry({
        id: input.newId(),
        timestamp: input.now,
        result,
        selection: input.selection,
        character: input.character,
        resolution,
        effective,
        manualMode: input.mode,
      }),
    ),
  };

  // RULE DCC-COND-005 - Stunned lasts "Once you make a Check" (Rules p.46).
  // Fired HERE, on the one pathway every Check goes through, so no screen or
  // button has to remember to do it.
  //
  // This used to also test `result.kind !== 'advancement'`, meaning to exclude
  // Advancement Checks. That test could never be false: `CheckKind` is
  // 'skill' | 'stat' | 'evade', and an Advancement Check is rolled by
  // `advancementService`, which does not come through here at all. The
  // condition was dead, and removing it changes no behaviour.
  const triggered = input.runtime
    ? resolveTrigger(input.runtime, 'after-next-check')
    : null;

  return {
    ok: true,
    character,
    session,
    runtime: triggered ? triggered.runtime : (input.runtime ?? null),
    expiredEffectIds: triggered ? triggered.expiredEffectIds : [],
    result,
    resolution,
    marked: character !== input.character,
  };
}

/**
 * An Evade Check. Evade is not a Skill, so nothing is marked.
 *
 * RESOLVED WHEN A DIFFICULTY IS SUPPLIED, exactly as `performRoll` does. This
 * used to record `resolution: null` unconditionally, which was harmless while
 * the only caller was the manual dice tool - it has no Difficulty to offer -
 * and became the one thing standing between C1's stored `evadeDifficulty` and a
 * resolved Evade.
 *
 * There is ONE Evade Check implementation and one history-entry builder. The
 * declaration-driven Evade Interrupt COMPOSES this per attack; it does not
 * reimplement it. The manual pathway still passes `difficulty: null` and still
 * records an unresolved Check.
 */
export function performEvadeRoll(
  roller: Roller,
  input: Omit<PerformRollInput, 'selection'> & { dexStat?: StatId },
): RollApplication {
  const effective = effectiveFor(input);
  const result = rollEvadeFor(roller, effective, input.mode);
  const selection: RollSelection = { stat: 'dex', skill: null };
  const resolution =
    input.difficulty === null ? null : resolveAgainstDifficulty(result, input.difficulty);

  const triggered = input.runtime ? resolveTrigger(input.runtime, 'after-next-check') : null;

  return {
    character: input.character,
    runtime: triggered ? triggered.runtime : (input.runtime ?? null),
    expiredEffectIds: triggered ? triggered.expiredEffectIds : [],
    session: {
      ...input.session,
      rollHistory: addRoll(
        input.session.rollHistory,
        toHistoryEntry({
          id: input.newId(),
          timestamp: input.now,
          result,
          selection,
          character: input.character,
          resolution,
          effective,
          manualMode: input.mode,
        }),
      ),
    },
    result,
    resolution,
    marked: false,
  };
}
